'use client';

import { useMemo } from 'react';

import { useSubsidiaryFilterOptional } from '@/@subsidiary/context';
import { useIsViewingAsClient } from '@/@subsidiary/hooks/useIsViewingAsClient';
import { viewAsListScope, type ViewAsListScope } from '@/@subsidiary/view-as-scope';

const EMPTY_SF: readonly string[] = [];
const EMPTY_ORGS: readonly number[] = [];

/**
 * The client a list is scoped to while this tab views as one
 * (`src/@subsidiary/view-as-scope.ts`). The org ids are the client's selected
 * orgs (its group when the hierarchy drawer widens them), or the picked org
 * until those are set.
 */
export function useViewAsListScope(): ViewAsListScope {
    const filter = useSubsidiaryFilterOptional();
    const isViewingAsClient = useIsViewingAsClient();
    const sfAccountIds = filter?.selectedSfAccountIds ?? EMPTY_SF;
    const selectedOrgIds = filter?.selectedOrgIds ?? EMPTY_ORGS;
    const masqueradeOrgId = filter?.masqueradeOrgId ?? null;

    return useMemo(() => {
        const orgIds =
            selectedOrgIds.length > 0
                ? selectedOrgIds
                : masqueradeOrgId != null
                  ? [masqueradeOrgId]
                  : EMPTY_ORGS;
        return viewAsListScope({ isViewingAsClient, sfAccountIds, orgIds });
    }, [isViewingAsClient, sfAccountIds, selectedOrgIds, masqueradeOrgId]);
}
